import { c as _c } from "react/compiler-runtime";
import { getHostedIdentifySessionRequirementsOptions, getHostedIdentifySessionRequirementsQueryKey, postHostedIdentifySessionMutation, postHostedUserConsentMutation } from '@onefootprint/fetch-hosted';
import type { ChallengeKind, IdentifiedUser } from '@onefootprint/request-types';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DifferentAccountOption } from '@/idv2/features/identify/different-account-option';
import { type ChallengeKindType, LoginChallengeSelect } from '@/idv2/features/identify/login-challenge-select';
import { TryAnotherWay } from '@/idv2/features/identify/try-another-way';
import { getValidatedIdentifyBootstrap } from '@/idv2/features/identify/validate-bootstrap';
import { setOnboardingSessionToken, setPasskeyRegistered } from '@/idv2/features/idv-to-idv2';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { isGetChallengeResponseError, runPasskeyChallenge } from '@/idv2/hooks/use-step-up/run-passkey-challenge';
import { checkDeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logError
} = getLogger({
  name: 'identify-login'
});
type LoginLoaderData = {
  loginRequirement: {
    user: IdentifiedUser;
  };
  methods: ChallengeKindType[];
  showDifferentAccount: boolean;
  showTryAnotherWay: boolean;
  displayTermsAndConditions: boolean;
};
export const Route = createFileRoute('/identify/login')({
  component: LoginPage,
  loader: async ({
    context
  }): Promise<LoginLoaderData> => {
    const headers = {
      'X-Fp-Authorization': context.onboardingSessionToken
    };
    const [requirementsData, device] = await Promise.all([context.queryClient.ensureQueryData(getHostedIdentifySessionRequirementsOptions({
      headers
    })), checkDeviceInfo()]);
    const loginRequirement = requirementsData?.requirements?.[0]?.kind === 'login' ? requirementsData.requirements[0] : undefined;
    if (!loginRequirement) {
      throw new Error('Reached /identify/login without a login requirement in the identify session');
    }
    const kinds = loginRequirement.user?.availableChallengeKinds ?? [];
    const otpMethods = kinds.filter(k => k === 'sms' || k === 'email');
    const canShowBiometric = kinds.includes('biometric') && device.hasSupportForWebauthn && (device.type === 'mobile' || !!loginRequirement.user?.hasSyncablePasskey);
    const methods: ChallengeKindType[] = canShowBiometric ? ['biometric', ...otpMethods] : otpMethods;
    const {
      isBootstrap
    } = getValidatedIdentifyBootstrap(context.kycBootstrapData);
    const playbookHidesOption = context.config?.flags?.shouldHideBootstrappedLoginWithDifferent ?? false;
    const isStaticMatch = loginRequirement.user.isStaticMatch ?? false;
    const isComponentsSdk = context.isComponentsSdk ?? false;
    const showDifferentAccount = isBootstrap && !playbookHidesOption && !isStaticMatch && !isComponentsSdk;
    const hasUnverifiedEmailAuth = loginRequirement.user?.authMethods?.some(m => m.kind === 'email' && m.isVerified === false);
    const showTryAnotherWay = !!hasUnverifiedEmailAuth && !loginRequirement.user?.isUnverified;
    const displayTermsAndConditions = requirementsData?.displayTermsAndConditions ?? false;
    return {
      loginRequirement,
      methods,
      showDifferentAccount,
      showTryAnotherWay,
      displayTermsAndConditions
    };
  }
});
function LoginPage() {
  const $0 = _c(50);
  const navigate = useNavigate();
  const footprint = useFootprint();
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation("idv2");
  const {
    config,
    onboardingSessionToken,
    obConfigAuth,
    queryClient,
    sandboxId: contextSandboxId,
    isComponentsSdk,
    scope
  } = Route.useRouteContext();
  const {
    loginRequirement,
    methods,
    showDifferentAccount,
    showTryAnotherWay,
    displayTermsAndConditions
  } = Route.useLoaderData();
  let t0;
  if ($0[0] !== onboardingSessionToken) {
    t0 = {
      mutationFn: () => runPasskeyChallenge(onboardingSessionToken)
    };
    $0[0] = onboardingSessionToken;
    $0[1] = t0;
  } else {
    t0 = $0[1];
  }
  const passkeyMutation = useMutation(t0);
  let t1;
  if ($0[2] !== onboardingSessionToken) {
    t1 = postHostedUserConsentMutation({
      headers: {
        "X-Fp-Authorization": onboardingSessionToken
      }
    });
    $0[2] = onboardingSessionToken;
    $0[3] = t1;
  } else {
    t1 = $0[3];
  }
  const consentMutation = useMutation(t1);
  let t2;
  if ($0[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = postHostedIdentifySessionMutation();
    $0[4] = t2;
  } else {
    t2 = $0[4];
  }
  const resetSessionMutation = useMutation(t2);
  let t3;
  if ($0[5] !== consentMutation || $0[6] !== displayTermsAndConditions || $0[7] !== footprint || $0[8] !== loginRequirement.user.scrubbedEmail || $0[9] !== loginRequirement.user.scrubbedPhone || $0[10] !== navigate || $0[11] !== passkeyMutation || $0[12] !== t || $0[13] !== toast) {
    t3 = async challengeKind => {
      if (challengeKind === "biometric") {
        ;
        try {
          await passkeyMutation.mutateAsync();
          if (displayTermsAndConditions) {
            await consentMutation.mutateAsync({
              body: {
                content: t(_temp),
                kind: "terms_and_conditions"
              }
            });
          }
          footprint.navigation.forward();
        } catch (t4) {
          const error = t4;
          const isPasskeyError = isGetChallengeResponseError(error);
          toast({
            title: t($ => isPasskeyError ? $.identify.login.passkeyError.title : $.errorFallback.genericError.title),
            description: t($_0 => isPasskeyError ? $_0.identify.login.passkeyError.description : $_0.errorFallback.genericError.subtitle),
            variant: "error"
          });
        }
        return;
      }
      const otpKind = challengeKind === "sms" || challengeKind === "email" ? challengeKind : "sms";
      navigate({
        to: "/identify/otp/$challengeKind",
        params: {
          challengeKind: otpKind
        },
        search: {
          scrubbedEmail: loginRequirement.user?.scrubbedEmail,
          scrubbedPhone: loginRequirement.user?.scrubbedPhone,
          fromChallengeSelector: true
        }
      });
    };
    $0[5] = consentMutation;
    $0[6] = displayTermsAndConditions;
    $0[7] = footprint;
    $0[8] = loginRequirement.user.scrubbedEmail;
    $0[9] = loginRequirement.user.scrubbedPhone;
    $0[10] = navigate;
    $0[11] = passkeyMutation;
    $0[12] = t;
    $0[13] = toast;
    $0[14] = t3;
  } else {
    t3 = $0[14];
  }
  const handleSelect = t3;
  let t4;
  if ($0[15] !== config?.isLive || $0[16] !== contextSandboxId || $0[17] !== isComponentsSdk || $0[18] !== navigate || $0[19] !== obConfigAuth || $0[20] !== onboardingSessionToken || $0[21] !== queryClient || $0[22] !== resetSessionMutation || $0[23] !== scope) {
    t4 = async () => {
      if (!obConfigAuth) {
        logError("handleReset called without obConfigAuth");
        return;
      }
      const isLive = config?.isLive ?? true;
      const sandboxId = !isLive ? contextSandboxId : undefined;
      const data = await resetSessionMutation.mutateAsync({
        body: {
          data: {},
          scope
        },
        headers: {
          ...obConfigAuth,
          ...(sandboxId ? {
            "X-Sandbox-Id": sandboxId
          } : {}),
          ...(isComponentsSdk === true ? {
            "X-Fp-Is-Components-Sdk": true
          } : {})
        }
      });
      queryClient.invalidateQueries({
        queryKey: getHostedIdentifySessionRequirementsQueryKey({
          headers: {
            "X-Fp-Authorization": onboardingSessionToken
          }
        })
      });
      setOnboardingSessionToken(data.token);
      setPasskeyRegistered(false);
      navigate({
        to: "/identify"
      });
    };
    $0[15] = config?.isLive;
    $0[16] = contextSandboxId;
    $0[17] = isComponentsSdk;
    $0[18] = navigate;
    $0[19] = obConfigAuth;
    $0[20] = onboardingSessionToken;
    $0[21] = queryClient;
    $0[22] = resetSessionMutation;
    $0[23] = scope;
    $0[24] = t4;
  } else {
    t4 = $0[24];
  }
  const handleReset = t4;
  const shouldShowWelcomeBack = !loginRequirement.user.isUnverified;
  let t5;
  if ($0[25] !== shouldShowWelcomeBack || $0[26] !== t) {
    t5 = shouldShowWelcomeBack ? t(_temp2) : t(_temp3);
    $0[25] = shouldShowWelcomeBack;
    $0[26] = t;
    $0[27] = t5;
  } else {
    t5 = $0[27];
  }
  const title = t5;
  let t6;
  if ($0[28] !== t) {
    t6 = t(_temp4);
    $0[28] = t;
    $0[29] = t6;
  } else {
    t6 = $0[29];
  }
  let t7;
  if ($0[30] !== t) {
    t7 = t(_temp5);
    $0[30] = t;
    $0[31] = t7;
  } else {
    t7 = $0[31];
  }
  const t8 = passkeyMutation.isPending || consentMutation.isPending;
  let t9;
  if ($0[32] !== handleSelect || $0[33] !== loginRequirement || $0[34] !== methods || $0[35] !== t6 || $0[36] !== t7 || $0[37] !== t8 || $0[38] !== title) {
    t9 = <LoginChallengeSelect loginRequirement={loginRequirement} methods={methods} onSelect={handleSelect} title={title} subtitle={t6} continueLabel={t7} isLoading={t8} />;
    $0[32] = handleSelect;
    $0[33] = loginRequirement;
    $0[34] = methods;
    $0[35] = t6;
    $0[36] = t7;
    $0[37] = t8;
    $0[38] = title;
    $0[39] = t9;
  } else {
    t9 = $0[39];
  }
  let t10;
  if ($0[40] !== navigate || $0[41] !== showTryAnotherWay) {
    t10 = showTryAnotherWay && <TryAnotherWay onClick={() => navigate({
      to: "/identify/kba"
    })} />;
    $0[40] = navigate;
    $0[41] = showTryAnotherWay;
    $0[42] = t10;
  } else {
    t10 = $0[42];
  }
  let t11;
  if ($0[43] !== handleReset || $0[44] !== showDifferentAccount) {
    t11 = showDifferentAccount && <DifferentAccountOption onReset={handleReset} />;
    $0[43] = handleReset;
    $0[44] = showDifferentAccount;
    $0[45] = t11;
  } else {
    t11 = $0[45];
  }
  let t12;
  if ($0[46] !== t10 || $0[47] !== t11 || $0[48] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $0[46] = t10;
    $0[47] = t11;
    $0[48] = t9;
    $0[49] = t12;
  } else {
    t12 = $0[49];
  }
  return t12;
}
function _temp5($_5) {
  return $_5.identify.login.continue;
}
function _temp4($_4) {
  return $_4.identify.login.logInOptions;
}
function _temp3($_3) {
  return $_3.identify.login.verifyTitle;
}
function _temp2($_2) {
  return $_2.identify.login.welcomeBackTitle;
}
function _temp($_1) {
  return $_1.identify.otp.legalFooter;
}